<template>
  <div>
    <DataTable
      ref="dataTable"
      :columns="columnList"
      defaultColumnOrderDir="ASC"
      :defaultColumnOrderIndex="5"
      :url="dataURL"
      :filterOptions="filterOptions"
      :hasDateRangeFilter="true"
      exportTitle="Production Panels">
      <template v-slot:title>
        <slot name="title" />
      </template>
      <template v-slot:tools>
        <slot name="tools" />
      </template>
      <template v-slot:tableHeaderCell="tableHeaderCell">
        <span v-if="tableHeaderCell.data == addRunsheetColumnIndex && selectionEnabled" class="ml-2">
          <FormButton class="btn-xxs btn-default mr-1" @click="selectAllVisible">Select All</FormButton>
          <FormButton class="btn-xxs btn-default ml-1" @click="unselectAllVisible">Unselect All</FormButton>
        </span>
      </template>
      <template v-slot:row="row">
        <tr :key="row.data.id">
          <td class="table-data-nav-link">
            <LinkProductionPanel :id="row.data.id" :name="row.data.barcode" :newTab="openNewTab" />
          </td>
          <td>
            <LabelPanelStatusBadge :status="row.data.status" :isActiveTransferTasksAssigned="row.data.isActiveTransferTasksAssigned" />
          </td>
          <td>
            {{row.data.panelHeight}} x {{row.data.panelWidth}}
          </td>
          <td>
            {{row.data.orderItem.stock.name}}
          </td>              
          <td>
            <LinkOrder :id="row.data.order.id" :name="row.data.order.sequenceNumber" />
          </td>
          <td>
            {{formatDate(row.data.dateDespatchDue)}}
          </td>
          <td>
            {{row.data.currentProductionStage ? row.data.currentProductionStage.name : '-'}}
          </td>
          <td>
            <span v-if="row.data.currentSupplier">
              <LinkContact :id="row.data.currentSupplier.id" :name="row.data.currentSupplier.name" />
            </span>
            <span v-if="!row.data.currentSupplier && row.data.inventoryBin">
              {{row.data.inventoryBin.code}} | {{row.data.inventoryBin.accountLocation.name}}
            </span>
            <i class="far fa-arrow-right mx-1" />
            <span v-if="row.data.currentProductionStage">
              <AccountLocationDisplay v-if="row.data.currentProductionStage.curStageTask.accountLocationId" :id="row.data.currentProductionStage.curStageTask.accountLocationId" />
              <span v-if="row.data.currentProductionStage.curStageTask.purchaseOrder">
                <LinkContact :id="row.data.currentProductionStage.curStageTask.purchaseOrder.contactId" :name="row.data.currentProductionStage.curStageTask.purchaseOrder.contact.name" />
                -
                <LinkPurchaseOrder :id="row.data.currentProductionStage.curStageTask.purchaseOrder.id" :name="row.data.currentProductionStage.curStageTask.purchaseOrder.sequenceNumber" />
              </span>
            </span>
            <span v-if="!row.data.currentProductionStage && row.data.order.accountLocationId">
              <AccountLocationDisplay :id="row.data.order.accountLocationId" />
            </span>
          </td>
          <td class="text-right" v-if="selectionEnabled">
            <FormCheckbox :value="isPanelSelected(row.data.id)" @change="updatePanelSelection(row.data.id, $event)" />
          </td>
        </tr>
      </template>
    </DataTable>
  </div>
</template>

<script>
import _ from 'lodash'
import Utils from '@/utils/utils'
import AccountLocationDisplay from '@/modules/admin/components/accountLocationDisplay'
import LabelPanelStatusBadge from '@/modules/production/components/labelPanelStatusBadge'

import { ProductionPanelStatusTypes, ProductionPanelStatusTypeLookup } from '@/store/types'

export default {
  name: 'TaskPanelTransferDataTable',
  model: {
    event: 'update'
  },
  components: {
    AccountLocationDisplay,
    LabelPanelStatusBadge
  },
  props: {
    openNewTab: {
      type: Boolean,
      default: false
    },
    value: Array,
    filterAccountLocationId: Number,
    selectionEnabled: {
      type: Boolean,
      default: true
    },
  },
  computed: {
    title() {
      return this.$route.meta.title
    },
    dataURL() {
      var params = {
        filter: {
          isPendingTransfer: 1
        }
      }
      // Either source or destination account location id
      if(this.filterAccountLocationId)
        params.filter['transferAccountLocationId'] = this.filterAccountLocationId

      return '/production/panels?' + $.param(params)
    },
    addRunsheetColumnIndex() {
      return 8
    }
  },
  data() {
    let columnList = _.compact([
      { title: "Panel", data: "id" },
        { title: "Status", data: "status" },
        { title: "Size", data: "size", sortable: false },
        { title: "Stock", data: "orderItem.stock.name" },
        { title: "Order", data: "order.id" },
        { title: "Due Date", data: "dateDespatchDue", dateFilter: true, type: 'datetime' },
        { title: "Current Stage", data: "currentProductionStage.name", sortable: false },
        { title: "Transfer Required", data: "", sortable: false },
        this.selectionEnabled ? { title: "Add Runsheet", data: "", sortable: false, export: false } : null,
    ])

    let filterOptions = [
      {
        field: 'status',
        name: 'Status',
        options: [
          { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.pending), value: ProductionPanelStatusTypes.pending, class: 'label-production-panel-status-pending' },
          { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.production), value: ProductionPanelStatusTypes.production, class: 'label-production-panel-status-production' },
          { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.recut), value: ProductionPanelStatusTypes.recut, class: 'label-production-panel-status-recut' },
          { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.despatch), value: ProductionPanelStatusTypes.despatch, class: 'label-production-panel-status-despatch' },
          { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.completed), value: ProductionPanelStatusTypes.completed, class: 'label-production-panel-status-completed' },
          { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.cancelled), value: ProductionPanelStatusTypes.cancelled, class: 'label-production-panel-status-cancelled' }
        ]
      },
      {
        field: 'isActiveTransferTasksAssigned',
        name: 'Tasks Assigned',
        columnIndex: 0,
        options: [
          { name: 'Tasks Assigned', value: '1', class: 'label-order-tasks-assigned' },
          { name: 'No Tasks Assigned', value: '0', class: 'label-order-tasks-noneassigned' }
        ]
      },
    ]    

    return {
      filterOptions: filterOptions,
      columnList: columnList,
      selectedPanelMap: {}
    }
  },
  watch: {
    value: function() {
      this.updateSelectedPanelMap()
    }
  },  
  mounted() {
    this.updateSelectedPanelMap()

    // Apply default filters
    this.$nextTick(() => {
      let defaultFilters = _.compact([
        { value: ProductionPanelStatusTypes.despatch, field: 'status' },
        { value: ProductionPanelStatusTypes.production, field: 'status' },
        { value: ProductionPanelStatusTypes.pending, field: 'status' },
        { value: '0', field: 'isActiveTransferTasksAssigned' }
      ])

      this.$refs.dataTable.applySelectedFilters(defaultFilters)
    })
  },
  methods: {
    isPanelSelected(panelId) {
      return this.selectedPanelMap[panelId]
    },
    updatePanelSelection(panelId, selected) {
      this.selectedPanelMap[panelId] = selected
      this.updatePanelList()
    },
    selectAllVisible() {
      this.$refs.dataTable.getRowListData().forEach(panel => {
        this.$set(this.selectedPanelMap, panel.id, true)
      })
      this.updatePanelList()
    },
    unselectAllVisible() {
      this.$refs.dataTable.getRowListData().forEach(panel => {
        this.$set(this.selectedPanelMap, panel.id, false)
      })
      this.updatePanelList()
    },
    updateSelectedPanelMap() {
      this.selectedPanelMap = {}
      if(this.value) {
        _.uniq(this.value).forEach(panelId => {
          this.selectedPanelMap[panelId] = true
        })
      }
    },
    updatePanelList() {
      this.$emit('update', Object.keys(this.selectedPanelMap).filter(panelId => this.selectedPanelMap[panelId]).map(panelId => parseInt(panelId)))
    },
  }
};
</script>
